import { useMemo } from 'react';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Check } from 'lucide-react';
import cn from 'classnames';

export interface WizardStep {
  id: number;
  label: string;
  description?: string;
}

interface WizardStepperProps {
  steps: WizardStep[];
  currentStep: number;
  onStepClick?: (stepId: number) => void;
  allowNavigation?: boolean;
}

export default function WizardStepper({
  steps,
  currentStep,
  onStepClick,
  allowNavigation = false
}: WizardStepperProps) {
  const { getColour } = useThemeContext();

  const getStepStatus = (stepId: number) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const handleStepClick = (stepId: number) => {
    if (!allowNavigation || !onStepClick) return;
    if (stepId <= currentStep) {
      onStepClick(stepId);
    }
  };

  /* eslint-disable no-restricted-syntax -- Semantic colors for wizard step states */
  const stepColors = useMemo(
    () => ({
      completed: {
        bg: '#10B981',
        border: '#10B981',
        text: '#FFFFFF',
        label: getColour('normalText')
      },
      current: {
        bg: '#3B82F6',
        border: '#3B82F6',
        text: '#FFFFFF',
        label: getColour('normalText')
      },
      upcoming: {
        bg: getColour('baseLayer'),
        border: getColour('border'),
        text: getColour('inactiveText'),
        label: getColour('inactiveText')
      }
    }),
    [getColour]
  );
  /* eslint-enable no-restricted-syntax */

  return (
    <nav aria-label="Progress" className="w-full">
      <div
        className="rounded-xl border px-3 py-2"
        style={{
          backgroundColor: getColour('secondLayer'),
          borderColor: getColour('border')
        }}
      >
        {/* Compact header row (optional space saver + better hierarchy) */}
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p
              className="text-[11px] leading-4"
              style={{ color: getColour('inactiveText') }}
            >
              Step {currentStep} of {steps.length}
            </p>
          </div>
        </div>

        {/* Steps */}
        <ol className="mt-2 flex items-center justify-between gap-2">
          {steps.map(step => {
            const status = getStepStatus(step.id);
            const colors = stepColors[status];
            const isClickable = allowNavigation && step.id <= currentStep;

            return (
              <li key={step.id} className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => handleStepClick(step.id)}
                  disabled={!isClickable}
                  className={cn(
                    'group flex w-full items-center gap-2 rounded-2xl px-2 py-1.5 text-left transition-colors',
                    isClickable
                      ? 'hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
                      : 'cursor-default'
                  )}
                  style={{
                    // keep it theme-friendly; hover uses utility, base uses theme
                    color: getColour('normalText'),
                    // ring color comes from CSS vars normally; keep fallback subtle
                    // (focus-visible ring classes remain unchanged in behavior)
                    // NOTE: doesn't change functionality, just visual polish
                    ...(isClickable
                      ? {
                          // ring + offset colors for themed backgrounds
                          // Tailwind uses currentColor; we keep ring subtle by not overriding it
                          // If you have a ring token, you can set it here.
                        }
                      : {})
                  }}
                >
                  {/* Dot / pill */}
                  <span className="relative flex items-center">
                    <span
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold transition-transform',
                        isClickable && 'group-hover:scale-105'
                      )}
                      style={{
                        backgroundColor: colors.bg,
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderColor: colors.border,
                        color: colors.text,
                        boxShadow:
                          status === 'current'
                            ? '0 0 0 3px rgba(59, 130, 246, 0.18)'
                            : status === 'completed'
                              ? '0 0 0 3px rgba(16, 185, 129, 0.14)'
                              : 'none'
                      }}
                    >
                      {status === 'completed' ? (
                        <Check className="h-4 w-4" strokeWidth={3} />
                      ) : (
                        step.id
                      )}
                    </span>
                  </span>

                  {/* Label (more modern: single line, tight leading, truncation) */}
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block truncate text-xs leading-4',
                        status === 'current' && 'font-semibold'
                      )}
                      style={{ color: colors.label }}
                      title={step.label}
                    >
                      {step.label}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}
